import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, Plus, Ticket, X } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/Button';
import EmptyState from '../../components/EmptyState';
import { useAuth } from '../../context/AuthContext';
import { formatCurrency } from '../../data/mockData';
import { COUPON_VALIDATION_ERRORS, createCoupon, listAllPromotions, updateCoupon, validateCoupon } from '../../api/promotions';
import CouponCard from './CouponCard';
import CouponFormDrawer from './CouponFormDrawer';
import styles from './Promotions.module.css';

const STATUS_TABS = [
  { key: '', label: 'All' },
  { key: 'ACTIVE', label: 'Active' },
  { key: 'PAUSED', label: 'Paused' },
  { key: 'INACTIVE', label: 'Inactive' },
  { key: 'EXPIRED', label: 'Expired' },
];

const NO_CTA_STATUSES = ['PAUSED', 'INACTIVE', 'EXPIRED'];

export default function Promotions() {
  const { user } = useAuth();
  const [coupons, setCoupons] = useState([]);
  const [statusTab, setStatusTab] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [togglingCode, setTogglingCode] = useState(null);

  const [testCode, setTestCode] = useState('');
  const [testSubtotal, setTestSubtotal] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testError, setTestError] = useState('');

  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const loadCoupons = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const list = await listAllPromotions(user.idToken, statusTab || undefined);
      setCoupons(list);
    } catch (err) {
      setError(err.message || 'Could not load coupons.');
    } finally {
      setLoading(false);
    }
  }, [user.idToken, statusTab]);

  useEffect(() => {
    loadCoupons();
  }, [loadCoupons]);

  const filteredCoupons = useMemo(() => {
    if (!dateFrom && !dateTo) return coupons;
    const from = dateFrom ? new Date(dateFrom) : null;
    const to = dateTo ? new Date(`${dateTo}T23:59:59.999`) : null;
    return coupons.filter((c) => {
      const start = c.startsAt ? new Date(c.startsAt) : null;
      const end = c.endsAt ? new Date(c.endsAt) : null;
      if (from && end && end < from) return false;
      if (to && start && start > to) return false;
      return true;
    });
  }, [coupons, dateFrom, dateTo]);

  const clearDateFilter = () => { setDateFrom(''); setDateTo(''); };

  const openAdd = () => { setEditing(null); setSaveError(''); setDrawerOpen(true); };
  const openEdit = (c) => { setEditing(c); setSaveError(''); setDrawerOpen(true); };

  const handleSave = async (data) => {
    setSaving(true);
    setSaveError('');
    try {
      if (editing) {
        await updateCoupon(editing.code, data, user.idToken);
      } else {
        await createCoupon(data, user.idToken);
      }
      setDrawerOpen(false);
      await loadCoupons();
    } catch (err) {
      setSaveError(err.message || 'Could not save this coupon.');
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (coupon) => {
    setTogglingCode(coupon.code);
    try {
      const nextStatus = coupon.status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE';
      await updateCoupon(coupon.code, { ...coupon, status: nextStatus }, user.idToken);
      await loadCoupons();
    } catch (err) {
      setError(err.message || 'Could not update this coupon.');
    } finally {
      setTogglingCode(null);
    }
  };

  const handleTest = async (e) => {
    e.preventDefault();
    setTesting(true);
    setTestError('');
    setTestResult(null);
    try {
      const result = await validateCoupon(
        { couponCode: testCode.trim().toUpperCase(), subtotal: Number(testSubtotal) || 0 },
        user.idToken
      );
      setTestResult(result);
    } catch (err) {
      setTestError(COUPON_VALIDATION_ERRORS[err.message] || err.message || 'Could not validate this coupon.');
    } finally {
      setTesting(false);
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Promotions"
        title="Coupons & Offers"
        subtitle="Run discounts that bring customers back — track usage against every code."
        actions={<Button icon={Plus} onClick={openAdd}>Create coupon</Button>}
      />

      <div className={styles.testCard}>
        <p className={styles.testTitle}>Test a coupon</p>
        <form className={styles.testForm} onSubmit={handleTest}>
          <input
            className={styles.testInput}
            placeholder="COUPON CODE"
            value={testCode}
            onChange={(e) => setTestCode(e.target.value)}
            style={{ textTransform: 'uppercase' }}
          />
          <input
            className={styles.testInput}
            type="number"
            min="0"
            placeholder="Subtotal (₹)"
            value={testSubtotal}
            onChange={(e) => setTestSubtotal(e.target.value)}
          />
          <Button type="submit" size="sm" disabled={!testCode.trim() || testing}>
            {testing ? 'Checking…' : 'Test'}
          </Button>
        </form>
        {testResult && (
          <p className={styles.testResultOk}>
            <CheckCircle2 size={13} /> Valid — {formatCurrency(testResult.discountAmount)} off, total {formatCurrency(testResult.totalAfterDiscount)}
          </p>
        )}
        {testError && (
          <p className={styles.testResultError}>
            <AlertTriangle size={13} /> {testError}
          </p>
        )}
      </div>

      <div className={styles.filterRow}>
        <div className={styles.filterTabs}>
          {STATUS_TABS.map(({ key, label }) => (
            <button
              key={key || 'all'}
              className={`${styles.filterTab} ${statusTab === key ? styles.filterTabActive : ''}`}
              onClick={() => setStatusTab(key)}
            >
              {label}
            </button>
          ))}
        </div>

        <div className={styles.dateFilters}>
          <label className={styles.dateFilterField}>
            <span>From</span>
            <input
              type="date"
              className={styles.dateInput}
              value={dateFrom}
              max={dateTo || undefined}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </label>
          <label className={styles.dateFilterField}>
            <span>To</span>
            <input
              type="date"
              className={styles.dateInput}
              value={dateTo}
              min={dateFrom || undefined}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </label>
          {(dateFrom || dateTo) && (
            <button className={styles.dateClearBtn} onClick={clearDateFilter} type="button">
              <X size={12} /> Clear
            </button>
          )}
        </div>
      </div>

      {loading && <p className={styles.loadingText}>Loading coupons…</p>}

      {!loading && error && (
        <EmptyState
          icon={AlertTriangle}
          title="Could not load coupons"
          description={error}
          action={<Button variant="outline" onClick={loadCoupons}>Retry</Button>}
        />
      )}

      {!loading && !error && coupons.length > 0 && filteredCoupons.length === 0 && (
        <EmptyState
          icon={Ticket}
          title="No coupons in this date range"
          description="Try widening or clearing the date filter."
          action={<Button variant="outline" onClick={clearDateFilter}>Clear date filter</Button>}
        />
      )}

      {!loading && !error && coupons.length === 0 && (
        NO_CTA_STATUSES.includes(statusTab) ? (
          <EmptyState
            icon={Ticket}
            title={`No ${STATUS_TABS.find((t) => t.key === statusTab)?.label} coupons`}
          />
        ) : (
          <EmptyState
            icon={Ticket}
            title="No coupons yet"
            description="Create a coupon to start offering discounts at checkout."
            action={<Button icon={Plus} onClick={openAdd}>Create coupon</Button>}
          />
        )
      )}

      {!loading && !error && filteredCoupons.length > 0 && (
        <div className={styles.grid}>
          {filteredCoupons.map((c) => (
            <CouponCard key={c.code} coupon={c} onEdit={openEdit} onToggle={toggleStatus} toggling={togglingCode === c.code} />
          ))}
        </div>
      )}

      <CouponFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSave={handleSave}
        coupon={editing}
        saving={saving}
        error={saveError}
      />
    </div>
  );
}
